import type { Action } from "@app/fx";
import { useDispatch } from "@app/react";
import type { Dispatch } from "react";
import { useEffect, useRef } from "react";
import { useVisibility } from "./use-visibility";

function useCancelPollOnUnmount(
  dispatch: Dispatch<Action>,
  cancelRef: React.RefObject<Action>,
) {
  useEffect(() => {
    return () => {
      dispatch(cancelRef.current);
    };
  }, [dispatch, cancelRef]);
}

/**
 * Manages poll action dispatching based on tab visibility and action changes.
 *
 * Key behavior:
 * - When tab is active on initial mount: dispatch action
 * - When tab becomes inactive: dispatch cancel to stop polling
 * - When action changes while tab is active: cancel old poll, then start new
 *
 * When the action changes (e.g., filter parameters change), we need to
 * explicitly cancel the old poll before starting the new one. starfx treats
 * poll actions with different payloads as independent instances, so without
 * cancellation they would run in parallel.
 *
 * We use a small setTimeout to ensure the cancel is processed before the
 * new action is dispatched. This avoids a race condition where the new action
 * gets lost while the supervisor is processing the cancel.
 */
function useSyncPollWithTabVisibility(
  dispatch: Dispatch<Action>,
  isTabActive: boolean,
  action: Action,
  cancel: Action,
) {
  const prevActionRef = useRef<Action | null>(null);

  useEffect(() => {
    if (isTabActive) {
      const isInitialMount = prevActionRef.current === null;
      const actionChanged = !isInitialMount && prevActionRef.current !== action;

      if (actionChanged) {
        // Cancel old poll and start new one with a small delay
        // The delay ensures the saga processes the cancel before seeing the new action
        dispatch(cancel);
        const timeoutIdToEnsureSagaProcessesCancelBeforeNewPoll = setTimeout(
          () => {
            dispatch(action);
          },
          50,
        );
        prevActionRef.current = action;
        return () =>
          clearTimeout(timeoutIdToEnsureSagaProcessesCancelBeforeNewPoll);
      }

      dispatch(action);
    } else {
      dispatch(cancel);
    }
    prevActionRef.current = action;
  }, [isTabActive, action, cancel, dispatch]);
}

export const usePoller = ({
  action,
  cancel,
}: {
  action: Action;
  cancel: Action;
}) => {
  const dispatch = useDispatch();
  const isTabActive = useVisibility();
  const cancelRef = useRef(cancel);
  cancelRef.current = cancel;

  useCancelPollOnUnmount(dispatch, cancelRef);
  useSyncPollWithTabVisibility(dispatch, isTabActive, action, cancel);
};
